import Cookies from "js-cookie";
import { Modal } from "antd";
import { create } from "zustand";
import { baseURL, setToken } from "../../../config/index";
import  secureLocalStorage  from  "react-secure-storage";
import { ENV } from "../../../config/env";
import {
  userLogin,
  forgotPasswordRequest,
  trayaGoogleLogin,
  getUserDetails,
  resetPasswordApi,
  sendResetPasswordOtpApi,
  loginOtpApi,
  getMyCallProviders
} from "../api_layer/api";
import { TOAST_MESSAGE_CSS, validateEmail } from "modules/utilities/utility";
import { getDeviceFingerprint } from "modules/utilities/fingerprint";
import { act } from "react-dom/test-utils";
import _ from 'lodash';

// If login-otp flagged the password as expiring within 3 days, block on a
// warning popup before proceeding (setUserDetails triggers a full-page
// redirect, so the modal must be acknowledged first). The warning is also
// stashed in secure storage so the post-login welcome page can show a
// persistent card (setUserDetails preserves it across its storage clear).
function showExpiryWarningThen(response, setUserDetails) {
  const expiryWarning = _.get(response, ["data", "passwordExpiryWarning"]);
  if (expiryWarning?.message) {
    secureLocalStorage.setItem(
      "passwordExpiryWarning",
      JSON.stringify(expiryWarning)
    );
    Modal.warning({
      title: "Password expiring soon",
      content: expiryWarning.message,
      okText: "Got it",
      onOk: () => setUserDetails(),
    });
  } else {
    secureLocalStorage.removeItem("passwordExpiryWarning");
    setUserDetails();
  }
}

export const userLoginStore = create((set) => ({
  res: "",
  isLoading: false,
  error: null,
  userData: null,
  otpFromResponse: null,
  fetchLoginData: async (
    loginEmail,
    password,
    setToaster,
    setUserDetails,
    navigate,
    validateUrl
  ) => {
    try {
      const [username, domain] = loginEmail.split("@");
      const [actualDomain, tld] = domain.split(".");
      if (validateUrl.includes(actualDomain) || domain.includes("maxicus") || /(localhost|dev)/.test(window.location.href)) {
        set({ isLoading: true });
        const response = await userLogin(loginEmail, password);

        // Check if response.data is a string or has a message property
        const message = typeof response?.data === 'string' ? response?.data : (response?.data?.message || '');
        const otpMatch = message.match(/OTP:\s*(\d{6})/);
        const extractedOtp = otpMatch ? otpMatch[1] : null;

        secureLocalStorage.setItem("otpEmail", loginEmail);

        // In dev environment with extracted OTP, directly call login API
        if (extractedOtp && ENV === 'dev') {
          const loginPayload = {
            email: loginEmail,
            otp: extractedOtp,
          };

          set({ isLoading: false });

          // Directly call loginWithOtp instead of navigating to OTP page
          try {
            const { fingerprint_id, is_incognito, fingerprint_details } = await getDeviceFingerprint();
            const response = await loginOtpApi({ ...loginPayload, fingerprint_id, is_incognito, fingerprint_details });
            Cookies.set("token", response.data.token);
            setToken(response.data.token);

            // Store isDeveloper from login-otp response if available
            if (response.data.isDeveloper !== undefined) {
              secureLocalStorage.setItem("isDeveloper", response.data.isDeveloper);
            }

            setToaster("Login", "You have logged in successfully", "success");
            showExpiryWarningThen(response, setUserDetails);
          } catch (error) {
            const errorMessage = error?.response?.data?.message || "Invalid OTP. Please try again.";
            setToaster("Login", errorMessage, "danger");
            // On failure, still navigate to OTP page so user can try manually.
            // Preserve ?next= so SAML SSO bounce flow survives the OTP detour.
            navigate(`/loginWithOtp${window.location.search}`);
          }
        } else {
          // Non-dev environment or no OTP extracted - navigate to OTP page as usual.
          // Preserve ?next= so SAML SSO bounce flow survives the OTP detour.
          if (extractedOtp) {
            secureLocalStorage.setItem("devOtp", extractedOtp);
          }
          set({ isLoading: false });
          navigate(`/loginWithOtp${window.location.search}`);
        }
      }
       else {
        setToaster("Login", "PLease login through google authentication", "success");
        secureLocalStorage.clear();
        // const token = response.data.token;
        // setToken(token);
        // document.cookie = `token=${token}`;
        // setUserDetails();
      }
    } catch (error) {
      // /login returns plain-text 401s and JSON 403s (e.g. password expired)
      const responseData = _.get(error, ["response", "data"]);
      const errorMessage =
        _.get(responseData, ["message"]) ||
        (typeof responseData === "string" && responseData.length
          ? responseData
          : "Failed to log in");
      set({
        error: errorMessage,
        isLoading: false,
      });
      setToaster("Login", errorMessage, "danger");
    }
  },
  forgotPassword: async (email, setToaster) => {
    try {
      const response = await forgotPasswordRequest(email);
      setToaster(
        "Forgot password",
        "Please check your mail for resetting password",
        "success"
      );
      set({ res: response.status });
    } catch (error) {
      setToaster(
        "Forgot password",
        <p className="mb-0 fw-bold">{error?.response?.data?.message}</p>,
        "danger"
      );
      set({ res: error?.response?.status });
    }
  },
}));

export const useGoogleLogin = create(() => ({
  googleLogin: async (googleData, setToaster, setUserDetails) => {
    try {
      const { fingerprint_id, is_incognito, fingerprint_details } = await getDeviceFingerprint();
      const response = await trayaGoogleLogin(googleData.tokenId, fingerprint_id, is_incognito, fingerprint_details);
      setToaster("login", "Login Successfully", "success");
      // SSO accounts have no Traya-managed password — drop any stale warning
      secureLocalStorage.removeItem("passwordExpiryWarning");
      Cookies.set("token", response.data.token);
      setToken(response.data.token);

      // Store isDeveloper from login response if available
      if (response.data.isDeveloper !== undefined) {
        secureLocalStorage.setItem("isDeveloper", response.data.isDeveloper);
      }

      setUserDetails();
    } catch (error) {
      setToaster("Login", "Failed to Login", "danger");
    }
  },
  sendResetPasswordOtp: async (id, toast) => {
    try {
      const response = await sendResetPasswordOtpApi(id);
      const message = _.get(response, ["data", "message"], "");
      const phone = _.get(response, ["data", "phone"], "");

      // In dev the backend echoes the OTP in the message (SMS doesn't go out)
      const otpMatch = message.match(/OTP:\s*(\d{6})/);
      const devOtp = otpMatch ? otpMatch[1] : null;

      toast.success(
        phone
          ? `OTP sent to your registered mobile ${phone}`
          : "OTP sent to your registered mobile",
        TOAST_MESSAGE_CSS
      );
      return { success: true, devOtp, phone };
    } catch (error) {
      const fallbackError = _.get(error, ["message"], "Failed to send OTP");
      const errorMessage = _.get(
        error,
        ["response", "data", "message"],
        fallbackError
      );
      toast.error(errorMessage, TOAST_MESSAGE_CSS);
      return { success: false, devOtp: null, phone: "" };
    }
  },
  resetPassword: async (id, payload, toast, navigate) => {
    try {
      await resetPasswordApi(id, payload);
      toast.success("Password reset successfully, please login", TOAST_MESSAGE_CSS);
      // Brief delay so the page's success step is visible before redirecting
      setTimeout(() => navigate("/login"), 1600);
      return true;
    } catch (error) {
      const fallbackError = _.get(error, ["message"], "Failed to reset password");
      const errorMessage = _.get(
        error,
        ["response", "data", "message"],
        fallbackError
      );
      toast.error(errorMessage, TOAST_MESSAGE_CSS);
      return false;
    }
  },
  loginWithOtp: async (payload, toast, setUserDetails) => {
    try {
      const { fingerprint_id, is_incognito, fingerprint_details } = await getDeviceFingerprint();
      const response = await loginOtpApi({ ...payload, fingerprint_id, is_incognito, fingerprint_details });
      Cookies.set("token", response.data.token);
      setToken(response.data.token);

      // Store isDeveloper from login-otp response if available
      if (response.data.isDeveloper !== undefined) {
        secureLocalStorage.setItem("isDeveloper", response.data.isDeveloper);
      }

      toast.success("You have logged in successfully", TOAST_MESSAGE_CSS);
      showExpiryWarningThen(response, setUserDetails);
      return true; // Return success status
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Invalid OTP. Please try again.";
      toast.error(errorMessage, TOAST_MESSAGE_CSS);
      // Don't clear storage completely, just remove the OTP related data
      secureLocalStorage.removeItem('devOtp');
      // Don't call setUserDetails on error, and don't refresh
      return false; // Return failure status
    }
  },
}));

export const useSetUserDetails = create((set) => ({
  userResponse: [],
  myCallProviders: [],
  setUserDetails: async () => {
    try {
      const userData = await getUserDetails();
      const callProviders = await getMyCallProviders()

      // Preserve isDeveloper from previous login before clearing storage
      const preservedIsDeveloper = secureLocalStorage.getItem("isDeveloper");
      // Preserve the password-expiry warning set at login so the welcome
      // page can render its warning card after the redirect
      const preservedExpiryWarning = secureLocalStorage.getItem("passwordExpiryWarning");

      const {
        id,
        email,
        first_name,
        house,
        team,
        desgination,
        shift,
        roles,
        alternate_email,
        level,
        is_consult_doctor,
        is_supervisor,
        voitekk_id,
        clinic_id,
        clinic_emp_id,
        is_first_login,
        is_not_clocked_out_yesterday,
        isDeveloper,
        roles: [
          {
            role: { id: roleID },
          },
        ],
      } = userData.data;

      secureLocalStorage.clear();
      secureLocalStorage.setItem("id", id);
      secureLocalStorage.setItem("email", email);
      secureLocalStorage.setItem("first_name", first_name);
      secureLocalStorage.setItem("alternate_email", alternate_email);
      secureLocalStorage.setItem("level", level);
      secureLocalStorage.setItem("is_supervisor", is_supervisor);
      secureLocalStorage.setItem("role", roleID);
      secureLocalStorage.setItem("obj", JSON.stringify(userData.data));
      secureLocalStorage.setItem("is_consult_doctor", Boolean(is_consult_doctor));
      secureLocalStorage.setItem("voitekk_id", voitekk_id);
      secureLocalStorage.setItem("popUp_Count", 0);
      secureLocalStorage.setItem("clinic_id", clinic_id);
      secureLocalStorage.setItem("clinic_emp_id", clinic_emp_id);
      secureLocalStorage.setItem("shift", shift);
      secureLocalStorage.setItem("is_first_login", is_first_login);
      secureLocalStorage.setItem("is_not_clocked_out_yesterday", is_not_clocked_out_yesterday);
      secureLocalStorage.setItem("house", house);
      secureLocalStorage.setItem("team", team);
      secureLocalStorage.setItem("designation", desgination);
      // Use preserved isDeveloper from login response (from loginOtpApi or googleLogin)
      secureLocalStorage.setItem("isDeveloper", preservedIsDeveloper);
      if (preservedExpiryWarning) {
        secureLocalStorage.setItem("passwordExpiryWarning", preservedExpiryWarning);
      }
      // Persist theme from user meta for cross-device consistency
      const userMeta = _.get(userData, ['data', 'meta'], {});
      const themeVal = userMeta.theme || "light";
      secureLocalStorage.setItem("theme", typeof themeVal === 'object' ? JSON.stringify(themeVal) : themeVal);

        //Getting the user call provider
        if (callProviders) {
          const providers = _.get(callProviders, ["data", "data"]);
  
          if (!_.isEmpty(providers)) {
            secureLocalStorage.setItem(
              "call_providers",
              JSON.stringify(providers)
            );
            set({ myCallProviders: providers });
          }
        }
      // Honor ?next= from the login URL (e.g., SAML SSO bounced us here).
      // Only same-origin relative paths are allowed — protocol-relative ("//...")
      // and absolute URLs are rejected to prevent open-redirect.
      const nextParam = new URLSearchParams(window.location.search).get("next");
      const safeNext = nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")
        ? nextParam
        : "/welcome";
      window.location.href = safeNext;
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  },
  getUserData: async () => {
    try {
      const userData = await getUserDetails();
      set({ userResponse: userData.data, ticketLoading: false });
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  }
}));
